import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faBolt } from '@fortawesome/free-solid-svg-icons';
import {
    StyledCard,
    CardHeader,
    StatusBadge,
    MonthSelector,
    ActionButton,
    SectionTitle
} from '../styles/BillingStyledComponents';
import { getStatusInfo } from '../utils/billingFormat';
import { computeGroupTotal } from '../utils/billingPolicyTree';
import { printBillingReceipt } from '../utils/printBillingReceipt';
import PolicyTreeAccordion from './PolicyTreeAccordion';
import ServiceGroupSection from './ServiceGroupSection';
import BillingFinalTable from './BillingFinalTable';
import BillingTotalsPanel from './BillingTotalsPanel';

/**
 * 청구 정보 단일 카드.
 *
 * 좌우 분할(BillingDetails | BillingSummary)을 대체하는 통합 컴포넌트.
 * 수직 섹션 구성:
 *   1. 헤더: 제목 + 월 선택 + 상태 배지
 *   2. PolicyTreeAccordion: 적용 정책명/버전 (접힘) + 펼치면 정책 트리 전체
 *   3. ServiceGroupSection × N: 정책 트리의 그룹 순회 — 사용량만 표기 (금액 없음)
 *   4. BillingFinalTable: 정책 트리 구조의 사용요금 vs 보전기준 정산 표
 *   5. BillingTotalsPanel: 사용량 기반 / 보전 증가분 / 최종금액
 *   6. 푸터: 중간 정산 / 영수증 인쇄하기 버튼
 *
 * 정책 트리(detail.pricingPolicyTree)가 null인 경우 fallback:
 *   - PolicyTreeAccordion은 메타데이터(name/version)만 표시
 *   - ServiceGroupSection 영역은 EmptyState
 */
const BillingPanel = ({
    detail,
    yearMonth,
    monthOptions = [],
    onMonthChange,
    isFinalAmount
}) => {
    const policyTree = detail?.pricingPolicyTree;
    const allGroups = policyTree?.serviceGroupList || [];
    // 빈 그룹(항목 없음) 또는 사용 합계 0인 그룹은 숨김 — CF/API처럼 정책에는 정의되어 있어도 실제 사용량이 없는 케이스 제외
    const groups = allGroups.filter(group => {
        const items = group.servicePriceList || [];
        if (items.length === 0) return false;
        return computeGroupTotal(group, detail) > 0;
    });

    // 그룹 펼침 상태 — 사용량 있는 그룹은 모두 기본 펼침
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());
    const [hasInterimSettlement, setHasInterimSettlement] = useState(false);

    const statusInfo = getStatusInfo(yearMonth, isFinalAmount, detail?.finalized_at);

    // 월 선택 옵션 파싱 (기존 BillingSummary.js 62-72에서 이전)
    const parsedMonths = monthOptions.map(opt => {
        if (typeof opt === 'string') {
            const parts = opt.split('_');
            const baseYearMonth = parts[0];
            const isFinalOpt = parts.includes('final');
            const finalizedDate = isFinalOpt && parts.length > 2 ? parts[2].split('T')[0] : null;
            const [year, month] = baseYearMonth.split('-').map(Number);
            return { year, month, value: opt, isFinal: isFinalOpt, finalizedDate };
        }
        return { ...opt, value: `${opt.year}-${String(opt.month).padStart(2, '0')}` };
    });

    const handleMonthSelect = (e) => {
        if (onMonthChange) onMonthChange(e.target.value);
    };

    // 중간 정산 가능 여부 (기존 BillingSummary.js 134-159에서 이전)
    const shouldShowInterimButton = () => {
        const now = new Date();
        const kstFormatter = new Intl.DateTimeFormat('ko-KR', {
            timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit'
        });
        const kstDateParts = kstFormatter.formatToParts(now);
        const currentYear = parseInt(kstDateParts.find(p => p.type === 'year').value);
        const currentMonth = parseInt(kstDateParts.find(p => p.type === 'month').value) - 1;
        const currentDay = parseInt(kstDateParts.find(p => p.type === 'day').value);

        const currentYearMonth = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
        const selectedYearMonth = yearMonth?.replace('_final', '') || currentYearMonth;
        if (selectedYearMonth !== currentYearMonth) return false;

        const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        const isNotEndOfMonth = currentDay < lastDayOfMonth;
        const todayKST = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;

        const hasInterimToday = monthOptions.some(opt => {
            if (typeof opt !== 'string') return false;
            const parts = opt.split('_');
            return parts.length === 3 && parts[1] === 'final' && parts[2].startsWith(todayKST);
        });

        return isNotEndOfMonth && !hasInterimToday && !hasInterimSettlement;
    };

    const handleInterimSettlement = async () => {
        const now = new Date();
        const kstNow = new Date(now.getTime() + (9 * 60 * 60 * 1000));
        const kstYesterday = new Date(kstNow.getTime() - 86400000);
        const year = kstNow.getFullYear();
        const month = String(kstNow.getMonth() + 1).padStart(2, '0');
        const start_date = `${year}-${month}-01`;
        const end_date = `${kstYesterday.getFullYear()}-${String(kstYesterday.getMonth() + 1).padStart(2, '0')}-${String(kstYesterday.getDate()).padStart(2, '0')}`;

        try {
            const { getConfig } = await import('../../../../config/getConfig');
            const config = getConfig();
            const apiBaseUrl = config.API_URL;
            const headers = { 'Content-Type': 'application/json' };
            const siteToken = sessionStorage.getItem('siteToken');
            const token = sessionStorage.getItem('token');
            if (siteToken) headers.Authorization = `Bearer ${siteToken}`;
            else if (token) headers.Authorization = `Bearer ${token}`;

            const res = await axios.post(
                `${apiBaseUrl}/billing/settlement?startDate=${start_date}&endDate=${end_date}&isFinalized=true`,
                {}, { headers }
            );

            if (res.status === 200 && res.data?.success && res.data?.report) {
                setHasInterimSettlement(true);
                alert('중간 정산이 완료되었습니다.');
                window.location.reload();
            } else {
                alert('중간 정산 요청에 실패했습니다.');
            }
        } catch (err) {
            alert('중간 정산 요청에 실패했습니다.');
        }
    };

    return (
        <StyledCard>
            <CardHeader style={{ justifyContent: 'flex-end' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {monthOptions.length > 0 && (
                        <MonthSelector
                            value={yearMonth || ''}
                            onChange={handleMonthSelect}
                            style={{ width: 'auto', minWidth: '180px' }}
                        >
                            {parsedMonths.map(opt => {
                                const now = new Date();
                                const kstNow = new Date(now.getTime() + (9 * 60 * 60 * 1000));
                                const isCurrentMonth = opt.year === kstNow.getFullYear() && opt.month === (kstNow.getMonth() + 1);
                                return (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.year}년 {opt.month}월
                                        {opt.isFinal ? ` 중간정산${opt.finalizedDate ? ` (${opt.finalizedDate})` : ''}` : (isCurrentMonth ? ' (이번달)' : '')}
                                    </option>
                                );
                            })}
                        </MonthSelector>
                    )}
                    <StatusBadge className={statusInfo.className}>{statusInfo.label}</StatusBadge>
                </div>
            </CardHeader>

            <div style={{ padding: '1.5rem' }}>
                <PolicyTreeAccordion
                    policyTree={policyTree}
                    pricingPolicyName={detail?.pricingPolicyName}
                    pricingPolicyVersion={detail?.pricingPolicyVersion}
                />

                <SectionTitle style={{ marginTop: '0.5rem' }}>그룹</SectionTitle>
                {groups.length > 0 ? (
                    groups.map(group => (
                        <ServiceGroupSection
                            key={group.uuid || group.serviceItemNumber}
                            group={group}
                            detail={detail}
                            expanded={!collapsedGroups.has(group.uuid || group.serviceItemNumber)}
                            onToggle={() => setCollapsedGroups(prev => {
                                const next = new Set(prev);
                                const key = group.uuid || group.serviceItemNumber;
                                if (next.has(key)) next.delete(key); else next.add(key);
                                return next;
                            })}
                        />
                    ))
                ) : (
                    <div style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: '#94a3b8',
                        background: '#f8fafc',
                        borderRadius: '12px'
                    }}>
                        정책 정보를 불러올 수 없습니다.
                    </div>
                )}

                <BillingFinalTable detail={detail} />

                <BillingTotalsPanel detail={detail} isFinalAmount={isFinalAmount} />

                <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'row', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {shouldShowInterimButton() && (
                        <ActionButton className="btn-info-action" onClick={handleInterimSettlement} style={{ width: 'auto', flex: '1 1 200px' }}>
                            <FontAwesomeIcon icon={faBolt} />
                            중간 정산
                        </ActionButton>
                    )}
                    {(isFinalAmount || hasInterimSettlement) && (
                        <ActionButton
                            className="btn-outline-action"
                            onClick={() => printBillingReceipt(detail, yearMonth, isFinalAmount)}
                            style={{ width: 'auto', flex: '1 1 200px' }}
                        >
                            <FontAwesomeIcon icon={faPrint} />
                            영수증 인쇄하기
                        </ActionButton>
                    )}
                </div>
            </div>
        </StyledCard>
    );
};

export default BillingPanel;
